import { Link } from "react-router-dom";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Pencil, Trash2, CalendarClock, Users, Building2 } from "lucide-react";

const STATUS = [{ v: "todo", l: "To do" }, { v: "in_progress", l: "In progress" }, { v: "completed", l: "Completed" }];
const priorityStyle = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-gray-100 text-gray-600 border-gray-200",
};
const statusDot = { todo: "bg-gray-300", in_progress: "bg-blue-500", completed: "bg-emerald-500" };

export function TaskRow({ task, onStatus, onEdit, onDelete, showClient = true }) {
  const overdue = task.due_date && task.status !== "completed" && new Date(task.due_date) < new Date(new Date().toDateString());
  const done = task.status === "completed";
  return (
    <div className="flex items-start gap-3 p-3 border border-gray-100 rounded-sm hover:bg-gray-50 transition-colors group" data-testid={`task-row-${task.id}`}>
      <span className={`w-2 h-2 rounded-full mt-2 shrink-0 ${statusDot[task.status] || "bg-gray-300"}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${done ? "text-gray-400 line-through" : "text-gray-900"}`}>{task.title}</p>
        {task.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{task.description}</p>}
        <div className="flex items-center gap-3 mt-1.5 flex-wrap text-xs">
          <span className={`font-semibold px-1.5 py-0.5 rounded-sm border capitalize ${priorityStyle[task.priority] || priorityStyle.medium}`}>{task.priority}</span>
          {task.due_date && (
            <span className={`flex items-center gap-1 ${overdue ? "text-red-600 font-semibold" : "text-gray-500"}`} data-testid={`task-due-${task.id}`}>
              <CalendarClock className="w-3 h-3" /> {new Date(task.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}{overdue ? " · overdue" : ""}
            </span>
          )}
          {task.assignees?.length > 0 && (
            <span className="flex items-center gap-1 text-gray-500"><Users className="w-3 h-3" /> {task.assignees.map((a) => a.name).join(", ")}</span>
          )}
          {showClient && task.client_id && (
            <Link to={`/clients/${task.client_id}`} className="flex items-center gap-1 text-[#002FA7] hover:underline">
              <Building2 className="w-3 h-3" /> {task.client_name || "Client"}
            </Link>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Select value={task.status} onValueChange={(v) => onStatus(task.id, v)}>
          <SelectTrigger data-testid={`task-status-select-${task.id}`} className="h-7 w-[130px] text-xs rounded-sm border-gray-200 px-2"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS.map((s) => <SelectItem key={s.v} value={s.v} className="text-xs">{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={() => onEdit(task)} title="Edit" data-testid={`task-edit-${task.id}`}
          className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
        <button onClick={() => onDelete(task)} title="Delete" data-testid={`task-delete-${task.id}`}
          className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
